import { useCallback, useEffect, useRef, useState } from 'react'

import type {
  HexaV2DropdownWithSearchChangeEvent,
  UseHexaV2DropdownWithSearchParams,
} from './types'

const resetSearchState = (
  setSearchTerm: (value: string) => void,
  setDebouncedSearchTerm: (value: string) => void
) => {
  setSearchTerm('')
  setDebouncedSearchTerm('')
}

const setElementPlaceholder = (
  element: Element | null | undefined,
  searchPlaceholder: string
) => {
  if (!element) return false

  element.setAttribute('placeholder', searchPlaceholder)

  const elementWithPlaceholder = element as HTMLElement & {
    placeholder?: string
  }

  if ('placeholder' in elementWithPlaceholder) {
    elementWithPlaceholder.placeholder = searchPlaceholder
  }

  return true
}

// Force the search placeholder to be set
const forceSearchPlaceholder = (
  dropdownMenu: HTMLElement,
  searchPlaceholder: string
) => {
  // Get the search element, search input element, and native input element
  const searchElement = dropdownMenu.shadowRoot?.querySelector('hexa-v2-search')
  const searchInputElement =
    searchElement?.shadowRoot?.querySelector('hexa-v2-input-text')
  const nativeInputElement =
    searchInputElement?.shadowRoot?.querySelector('input')

  // Set the search placeholder for the search element, search input element, and native input element
  const wasSearchPlaceholderSet = setElementPlaceholder(
    searchElement,
    searchPlaceholder
  )
  const wasSearchInputPlaceholderSet = setElementPlaceholder(
    searchInputElement,
    searchPlaceholder
  )
  const wasNativeInputPlaceholderSet = setElementPlaceholder(
    nativeInputElement,
    searchPlaceholder
  )

  // Return the result of the search placeholder being set
  return (
    wasSearchPlaceholderSet ||
    wasSearchInputPlaceholderSet ||
    wasNativeInputPlaceholderSet
  )
}

const useHexaV2DropdownWithSearch = ({
  className,
  disabled = false,
  items,
  onChange,
  placeholder,
  searchPlaceholder,
  value,
}: UseHexaV2DropdownWithSearchParams) => {
  const dropdownMenuRef = useRef<HTMLElement | null>(null)
  // Store the search placeholder for the dropdown instance
  const syncedSearchPlaceholderRef = useRef<{
    dropdownInstanceKey: number
    dropdownMenu: HTMLElement | null
    searchPlaceholder?: string
  }>({
    dropdownInstanceKey: -1,
    dropdownMenu: null,
  })
  const [dropdownInstanceKey, setDropdownInstanceKey] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const dropdownClassName = [
    className,
    disabled ? 'hexa-v2-dropdown-with-search--disabled' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const selectedItem = items.find(item => item.id === value || item.selected)
  const selectedLabel = selectedItem?.label ?? placeholder ?? ''

  useEffect(() => {
    const timer = globalThis.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => globalThis.clearTimeout(timer)
  }, [searchTerm])

  const syncSearchPlaceholder = useCallback(() => {
    const dropdownMenu = dropdownMenuRef.current

    if (!dropdownMenu || !searchPlaceholder) return false

    // Check if the search placeholder has already been synced for this dropdown instance
    const alreadySynced =
      syncedSearchPlaceholderRef.current.dropdownInstanceKey ===
        dropdownInstanceKey &&
      syncedSearchPlaceholderRef.current.dropdownMenu === dropdownMenu &&
      syncedSearchPlaceholderRef.current.searchPlaceholder === searchPlaceholder

    if (alreadySynced) return true

    // Force the search placeholder to be set
    const wasPlaceholderSynced = forceSearchPlaceholder(
      dropdownMenu,
      searchPlaceholder
    )

    // If the search placeholder was set, update the syncedSearchPlaceholderRef
    if (wasPlaceholderSynced) {
      syncedSearchPlaceholderRef.current = {
        dropdownInstanceKey,
        dropdownMenu,
        searchPlaceholder,
      }
    }

    // Return the result of the search placeholder being set
    return wasPlaceholderSynced
  }, [dropdownInstanceKey, searchPlaceholder])

  const handleDropdownOpen = useCallback(() => {
    // // Workaround for Honeycomb hardcoding the search placeholder as "Search"
    syncSearchPlaceholder()
  }, [syncSearchPlaceholder])

  const filteredItems = items.filter(item => {
    if (!debouncedSearchTerm) return true

    return item.label.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  })

  const handleOptionChange = (event: HexaV2DropdownWithSearchChangeEvent) => {
    onChange(event)

    const selectedValue = event.detail.value

    if (
      selectedValue &&
      typeof selectedValue !== 'string' &&
      selectedValue.optionType === 'single' &&
      selectedValue.selected
    ) {
      resetSearchState(setSearchTerm, setDebouncedSearchTerm)
      setDropdownInstanceKey(current => current + 1)
    }
  }

  const handleInputSearch = (event: HexaV2DropdownWithSearchChangeEvent) => {
    const searchValue =
      typeof event.detail.value === 'string' ? event.detail.value : ''

    setSearchTerm(searchValue)
  }

  const handleClearSearch = () => {
    resetSearchState(setSearchTerm, setDebouncedSearchTerm)
  }

  return {
    dropdownClassName,
    dropdownInstanceKey,
    dropdownMenuRef,
    filteredItems,
    handleClearSearch,
    handleDropdownOpen,
    handleInputSearch,
    handleOptionChange,
    selectedLabel,
  }
}

export default useHexaV2DropdownWithSearch
